import { Link } from "@shared/client/navigation";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { useMemo } from "react";
import { useIntlLocale, useT } from "@shared/client/locale";

/**
 * Bilan d'un passage : ce que la veille a changé, en trois nombres.
 *
 * Avant, la fin d'une analyse déroulait le rapport complet dans le tableau de
 * bord — un document de plusieurs écrans pour une information qui tient en une
 * ligne, et qui existait déjà sous forme de leads. Le rapport reste consultable
 * (« le passage en détail »), mais la destination est le pipeline.
 *
 * Le bilan vit à l'intérieur de la carte de veille (une section, pas une
 * seconde carte) : c'est le résultat de ce que la carte pilote.
 */

export type PassSummaryData = {
  runId: string;
  signals: number;
  newLeads: number;
  reactivated: number;
  knownLeads: number;
};

function Figure({ value, label, emphasis = false }: { value: number; label: string; emphasis?: boolean }) {
  return (
    <span className="grid gap-0.5">
      <strong className={cn("text-[24px] leading-none font-semibold tracking-[-0.02em] tabular-nums", !emphasis && "text-foreground/80")}>
        {value}
      </strong>
      <small className="text-[12.5px] text-muted-foreground">{label}</small>
    </span>
  );
}

export function PassSummary({
  summary,
  passedAt,
  fresh = false,
  className,
}: {
  summary: PassSummaryData;
  passedAt: string | null;
  fresh?: boolean;
  className?: string;
}) {
  const copy = useT().dashboard.passSummary;
  const intlLocale = useIntlLocale();
  const dayFormat = useMemo(
    () => new Intl.DateTimeFormat(intlLocale, { weekday: "long", day: "numeric", month: "long" }),
    [intlLocale],
  );
  const nothingNew = summary.newLeads === 0 && summary.reactivated === 0;

  return (
    <section
      aria-label={copy.label}
      className={cn("grid gap-4", className)}
      data-fresh={fresh || undefined}
      data-new-leads={summary.newLeads}
      data-tour="pass-summary"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="m-0 text-[13px] font-medium">
          {fresh ? copy.completed : copy.last}
          {passedAt && (
            <time className="font-normal text-muted-foreground" dateTime={passedAt}>
              {" "}· {dayFormat.format(new Date(passedAt))}
            </time>
          )}
        </p>
        <Link
          className="inline-flex items-center gap-1 text-[12.5px] text-muted-foreground hover:text-foreground"
          href={`/app/reports/${summary.runId}`}
        >
          {copy.details} <ArrowUpRight size={13} aria-hidden="true" />
        </Link>
      </div>

      <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
        <Figure emphasis label={copy.newLeads(summary.newLeads)} value={summary.newLeads} />
        <Figure label={copy.reactivated(summary.reactivated)} value={summary.reactivated} />
        <Figure label={copy.known(summary.knownLeads)} value={summary.knownLeads} />
        <p className="m-0 max-w-[340px] text-[12.5px] leading-relaxed text-muted-foreground md:ml-auto">
          {nothingNew ? copy.nothingNew(summary.signals) : copy.sourced(summary.signals)}
        </p>
      </div>
    </section>
  );
}
